import type { MutableRefObject } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AudioLines,
  Captions,
  ChevronDown,
  MonitorPlay,
  Sparkles,
  Video,
} from "lucide-react";
import { type Identifier, useNotify, useTranslate } from "ra-core";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type { ZoomRecording } from "../types";
import {
  downloadUrl,
  isMedia,
  pickInitialTrack,
  playableRecordings,
  type PlayerControl,
} from "../zoom_meeting_summaries/RecordingPlayer";
import { assetMessage } from "./assetMessages";
import {
  type AssetDisplayState,
  type AssetKind,
  type TrackKind,
} from "./meetingAssets";
import { absoluteMeetingLink } from "./meetingLink";
import type { MeetingAssets } from "./useMeetingAssets";

const ICONS: Record<AssetKind, LucideIcon> = {
  screen: MonitorPlay,
  camera: Video,
  audio: AudioLines,
  transcript: Captions,
  summary: Sparkles,
};

// The same colours the list's icons use, so a state reads the same everywhere.
const STATE_CLASS: Record<AssetDisplayState, string> = {
  available: "text-foreground",
  processing: "text-amber-600 dark:text-amber-400",
  failed: "text-destructive",
  waiting: "text-muted-foreground",
  none: "text-muted-foreground/60",
};

const TRACK_KINDS: readonly AssetKind[] = ["screen", "camera", "audio"];

const isTranscriptRow = (r: ZoomRecording) =>
  r.recording_type === "audio_transcript" ||
  r.recording_type === "closed_caption";

// The file an asset's Dropbox actions act on: for a track kind, the track Play
// would start on; for the transcript, the row whose text the panel shows.
const fileFor = (
  kind: AssetKind,
  recordings: ZoomRecording[] | undefined,
): ZoomRecording | undefined => {
  if (TRACK_KINDS.includes(kind)) {
    return pickInitialTrack(
      playableRecordings(recordings).filter((r) => isMedia(r.file_type)),
      kind as TrackKind,
    ) as ZoomRecording | undefined;
  }
  if (kind === "transcript") {
    return recordings?.find(
      (r) =>
        isTranscriptRow(r) && r.transcript_content && r.dropbox_shared_link,
    );
  }
  return undefined;
};

/**
 * Every asset of a meeting in one row under the header, each with the ways to
 * get it: play or show it here, download it, open it in Dropbox, or copy a link
 * to share. A row of chips rather than a panel, because the meeting page is a
 * fixed-height grid with no room to spare.
 *
 * `hasPanels` is false on the recordings-only page, which has no transcript or
 * summary panel to show.
 */
export const MeetingAssetBar = ({
  meetingId,
  assets,
  playerRef,
  hasPanels = true,
}: {
  meetingId: Identifier;
  assets: MeetingAssets;
  playerRef?: MutableRefObject<PlayerControl | null>;
  hasPanels?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [, setSearchParams] = useSearchParams();

  // Keeps any other params, such as a link's `t`, so the transcript highlight
  // survives picking an asset from the bar.
  const target = (kind: AssetKind) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set("asset", kind);
        return next;
      },
      { replace: true },
    );

  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(message, { type: "info" });
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 shrink-0 pb-2">
      {assets.kinds.map((kind) => {
        const state = assets.state(kind);
        const Icon = ICONS[kind];
        const name = translate(`resources.zoom_meetings.assets.${kind}`);
        const stateWord = translate(
          `resources.zoom_meetings.asset_state.${state}`,
        );
        const message = assetMessage(kind, state);
        const file =
          state === "available" ? fileFor(kind, assets.recordings) : undefined;
        const link = file?.dropbox_shared_link ?? undefined;
        const isTrack = TRACK_KINDS.includes(kind);
        const canShow = state === "available" && !isTrack && hasPanels;

        return (
          <DropdownMenu key={kind}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 px-2 cursor-pointer"
                aria-label={`${name}: ${stateWord}`}
              >
                <Icon className={cn("h-3.5 w-3.5", STATE_CLASS[state])} />
                <span className="text-xs">{name}</span>
                <span className={cn("text-xs", STATE_CLASS[state])}>
                  {stateWord}
                </span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-w-72">
              {message && (
                <DropdownMenuItem disabled className="whitespace-normal">
                  {translate(message.key, {
                    asset: translate(message.args.asset),
                  })}
                </DropdownMenuItem>
              )}
              {state === "available" && isTrack && playerRef && (
                <DropdownMenuItem
                  onSelect={() => {
                    target(kind);
                    playerRef.current?.playKind(kind as TrackKind);
                  }}
                >
                  {translate("resources.zoom_meetings.asset_actions.play")}
                </DropdownMenuItem>
              )}
              {canShow && (
                <DropdownMenuItem onSelect={() => target(kind)}>
                  {translate("resources.zoom_meetings.asset_actions.show")}
                </DropdownMenuItem>
              )}
              {link && (
                <>
                  <DropdownMenuItem asChild>
                    <a href={downloadUrl(link)} download>
                      {translate(
                        "resources.zoom_meetings.asset_actions.download",
                      )}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      {translate(
                        "resources.zoom_meetings.asset_actions.open_dropbox",
                      )}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      copy(
                        link,
                        "resources.zoom_meetings.asset_notify.dropbox_copied",
                      )
                    }
                  >
                    {translate(
                      "resources.zoom_meetings.asset_actions.copy_dropbox",
                    )}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  copy(
                    absoluteMeetingLink(meetingId, { asset: kind }),
                    "resources.zoom_meetings.asset_notify.atomic_copied",
                  )
                }
              >
                {translate("resources.zoom_meetings.asset_actions.copy_atomic")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </div>
  );
};
